import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PreKPracticeSettings } from '../types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';
import { useAuth } from '../../../context/AuthContext';
import { setUserProgress } from '../../../config/api';
import { SetUserProgressRequest } from '../../../types/progress';
import { CountingDots } from './types';
import { NUMERAL_FONT_STYLE } from '../../PreKAssessment/preKConstants';
import {
  PracticeHeader,
  PRACTICE_PHASES,
  PRACTICE_TIMER_CONSTANTS,
  generatePracticeAnswerChoices,
  PREK_PRACTICE_TRACKS,
  PRACTICE_VISUAL_CONFIG,
} from '../shared';

interface YouDoScreenProps {
  settings: PreKPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];
  onComplete: () => void;
  onBack: () => void;
}


const YouDoScreen: React.FC<YouDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const { user } = useAuth();
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedQueue, setFailedQueue] = useState<{ factId: string; digit: number }[]>([]);
  const [isRetryPhase, setIsRetryPhase] = useState(false);
  const [currentRetryIndex, setCurrentRetryIndex] = useState(0);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);
  const [currentPattern, setCurrentPattern] = useState<CountingDots | null>(null);
  const [isFirstAttempt, setIsFirstAttempt] = useState(true);

  const { playTTSWithCallback, playChime, isTTSPlaying } = usePreKTTS();

  // Generate dot pattern for given quantity
  const generateCountingDots = useCallback((quantity: number): CountingDots => {
    const gridPositions = PRACTICE_VISUAL_CONFIG.COUNTING_GRID;

    // Shuffle positions and select the first 'quantity' positions
    const shuffledPositions = [...gridPositions].sort(() => Math.random() - 0.5);
    const selectedPositions = shuffledPositions.slice(0, quantity);

    // Create dots from selected positions
    const dots = selectedPositions.map((pos, i) => ({
      x: pos.x,
      y: pos.y,
      id: `dot-${i}`
    }));

    return {
      id: `pattern-${Date.now()}-${Math.random()}`,
      quantity,
      dots
    };
  }, []);

  // Get current fact
  const getCurrentFact = useCallback(() => {
    if (isRetryPhase) {
      return failedQueue[currentRetryIndex];
    }
    return factsToPractice[currentFactIndex];
  }, [isRetryPhase, failedQueue, currentRetryIndex, factsToPractice, currentFactIndex]);

  const currentFact = getCurrentFact();
  const currentQuantity = currentFact?.digit; // Using 'digit' field but it contains quantity

  // Initialize answer choices and pattern when quantity or fact changes
  useEffect(() => {
    if (currentQuantity !== undefined) {
      setAnswerChoices(generatePracticeAnswerChoices(PREK_PRACTICE_TRACKS.COUNTING, currentQuantity));
      setCurrentPattern(generateCountingDots(currentQuantity));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setCorrectAnswerGiven(false);
      setIsFirstAttempt(true); // Reset to first attempt for new question
    }
  }, [currentQuantity, currentFact, generateCountingDots]);

  // Play TTS instruction when component loads
  useEffect(() => {
    if (!ttsHasPlayed && settings.speechEnabled && currentQuantity !== undefined) {
      playTTSWithCallback(QUESTION_SCREEN_AUDIO.counting.instruction, () => {
        setTtsHasPlayed(true);
      }).catch(() => {
        setTtsHasPlayed(true);
      });
    } else if (!settings.speechEnabled) {
      setTtsHasPlayed(true);
    }
  }, [currentQuantity, settings.speechEnabled, playTTSWithCallback, ttsHasPlayed]);

  const advanceToNext = useCallback(() => {
    if (isRetryPhase) {
      // In retry phase - after removing an item, check if queue is empty
      if (failedQueue.length === 0) {
        // Queue is empty, we're done!
        onComplete();
      } else if (currentRetryIndex >= failedQueue.length) {
        // Current index is beyond queue length, reset to 0
        setCurrentRetryIndex(0);
      } else if (currentRetryIndex < failedQueue.length - 1) {
        // Move to next item in retry queue
        setCurrentRetryIndex(currentRetryIndex + 1);
      } else {
        // At end of retry queue, restart from beginning
        setCurrentRetryIndex(0);
      }
    } else {
      // In original phase
      if (currentFactIndex < factsToPractice.length - 1) {
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        // Finished original sequence
        if (failedQueue.length > 0) {
          // Start retry phase
          setIsRetryPhase(true);
          setCurrentRetryIndex(0);
        } else {
          // All done!
          onComplete();
        }
      }
    }

    // Reset answer state for new question
    setHasAnswered(false);
    setCorrectAnswerGiven(false);
    setTtsHasPlayed(false);
    setIsFirstAttempt(true);
  }, [isRetryPhase, currentRetryIndex, failedQueue.length, currentFactIndex, factsToPractice.length, onComplete]);

  const handleAnswerClick = useCallback((selectedQuantity: number) => {
    // Don't allow clicks while TTS is playing or already answered
    if (isTTSPlaying || hasAnswered) return;

    setHasAnswered(true);
    const isCorrect = selectedQuantity === currentQuantity;

    if (isCorrect) {
      setCorrectAnswerGiven(true);

      // Only count score and progression for genuine first attempts
      if (isFirstAttempt) {
        // Update fact score: +1 for correct (only on first attempt)
        setFactScores(prev => {
          const newScores = new Map(prev);
          const currentScore = newScores.get(currentFact.factId) || 0;
          const newScore = currentScore + 1;
          newScores.set(currentFact.factId, newScore);

          // Only increment counter if going from 0 to 1 (first genuine correct)
          if (currentScore === 0 && newScore === 1) {
            setCompletedCount(prevCount => prevCount + 1);

            // Update backend fact status to accuracyPractice when score reaches 1
            if (user?.userId) {
              setTimeout(async () => {
                try {
                  const progressUpdate: SetUserProgressRequest = {
                    facts: {
                      [currentFact.factId]: {
                        status: 'accuracyPractice'
                      }
                    }
                  };

                  await setUserProgress(user.userId, progressUpdate);
                } catch (error) {
                  console.error('Failed to update fact status to accuracyPractice:', error);
                }
              }, 0);
            }
          }

          return newScores;
        });
      }
      // If not first attempt, still mark as correct but don't update score/progress

      if (isRetryPhase) {
        // In retry phase - remove from failed queue
        setFailedQueue(prev => {
          const newQueue = prev.filter((_, index) => index !== currentRetryIndex);
          // Check if we need to complete after this removal
          if (newQueue.length === 0) {
            setTimeout(() => onComplete(), 600);
          } else {
            // Adjust currentRetryIndex after removal
            setTimeout(() => {
              if (currentRetryIndex >= newQueue.length) {
                setCurrentRetryIndex(0);
              }
            }, 600);
          }
          return newQueue;
        });
      }

      // Play chime and advance after a delay (but not in retry phase where we handle advancement in setFailedQueue)
      playChime().then(() => {
        if (!isRetryPhase) {
          setTimeout(() => {
            advanceToNext();
          }, 500);
        }
      });
    } else {
      // Incorrect answer

      // Only update score on first attempt
      if (isFirstAttempt) {
        // Update fact score: -1 for incorrect (only on first attempt)
        setFactScores(prev => {
          const newScores = new Map(prev);
          const currentScore = newScores.get(currentFact.factId) || 0;
          const newScore = currentScore - 1;
          newScores.set(currentFact.factId, newScore);

          // If score goes below 1, decrement counter (was previously counted but now incorrect)
          if (currentScore >= 1 && newScore < 1) {
            setCompletedCount(prevCount => prevCount - 1);
          }

          return newScores;
        });
      }

      // Mark that first attempt is over (whether it was right or wrong)
      setIsFirstAttempt(false);

      if (!correctAnswerGiven) {
        // First attempt was wrong - add to failed queue (only if not already there)
        if (!isRetryPhase) {
          setFailedQueue(prev => {
            const alreadyExists = prev.some(f => f.factId === currentFact.factId);
            if (!alreadyExists) {
              return [...prev, currentFact];
            }
            return prev;
          });
        } else {
          // In retry phase and still wrong - add back to end of failed queue
          const failedFact = failedQueue[currentRetryIndex];
          setFailedQueue(prev => [...prev.filter((_, index) => index !== currentRetryIndex), failedFact]);
        }
      }

      // Reset to allow another attempt after feedback
      setTimeout(() => {
        setHasAnswered(false);
      }, PRACTICE_TIMER_CONSTANTS.WRONG_ANSWER_RETRY_MS);
    }
  }, [currentQuantity, hasAnswered, isTTSPlaying, playChime, correctAnswerGiven, isRetryPhase, currentFact, failedQueue, currentRetryIndex, advanceToNext, onComplete, user?.userId, isFirstAttempt]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.YOU_DO}
        onBack={onBack}
        progress={completedCount / factsToPractice.length}
      />

      {/* Main Content Area - Side-by-side layout */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        {/* Side-by-side layout container */}
        <div className="w-full max-w-6xl flex items-center justify-center gap-24">

          {/* Left Side: Dots Container - 4x8 grid (4 columns, 8 rows) */}
          {currentPattern && (
            <div className="flex-shrink-0" style={{ width: '200px', height: '400px' }}>
              <div className="relative w-full h-full">
                {/* Dots positioned absolutely in 4x8 grid */}
                {currentPattern.dots.map((dot) => (
                  <motion.div
                    key={dot.id}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="absolute w-6 h-6 lg:w-8 lg:h-8 bg-blue-600 rounded-full shadow-lg"
                    style={{
                      left: `${dot.x}%`,
                      top: `${dot.y}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Right Side: Answer Choices - 2x2 grid */}
          <div className="w-full max-w-md lg:max-w-lg">
            <div className="grid grid-cols-2 gap-8 lg:gap-12">
              {answerChoices.map((choice, index) => (
                <motion.button
                  key={choice}
                  initial={{ opacity: 0, y: 30, rotate: -5 }}
                  animate={{ opacity: 1, y: 0, rotate: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={!hasAnswered && !isTTSPlaying ? { scale: 1.08, rotate: 1 } : {}}
                  whileTap={!hasAnswered && !isTTSPlaying ? { scale: 0.92 } : {}}
                  onClick={() => handleAnswerClick(choice)}
                  disabled={hasAnswered || isTTSPlaying}
                  className={`
                    h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                    ${hasAnswered && choice === currentQuantity
                      ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                      : hasAnswered || isTTSPlaying
                      ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer hover:shadow-2xl transform-gpu'
                    }
                    ${hasAnswered || isTTSPlaying ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
                  `}
                  style={NUMERAL_FONT_STYLE}
                >
                  {choice}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YouDoScreen;